import useApi from "../../index";
import useTypes from "../../../strings/types";
import useConfig from "../../../config";

const useAuthActions = () => {
  // Services
  const { useServices } = useApi();
  const { useAuthServices } = useServices();
  const {
    loginService,
    sendMfaTokenService,
    forgotPasswordService,
    recoveryPasswordService,
    validLoginService,
    logOutService,
    recoveryPassworAppdService,
  } = useAuthServices();

  // Keycloak
  const { useKeycloak } = useConfig();
  const {
    keycloakLogin,
    getKeycloakUserInfo,
    getKeycloakToken,
    keycloakLogout,
  } = useKeycloak();

  // Strings
  const { useAuthTypes } = useTypes();
  const { LOGIN, LOGOUT, SET_AUTH_TOKEN, SET_MFA_PENDING, CLEAR_MFA_PENDING, UPDATE_PERMISSIONS, UPDATE_SESSION } = useAuthTypes();

  const actLogin =
    ({ email, password, mfa_token }, onSuccess, onError) =>
    async (dispatch) => {
      try {
        // Step 1: Get auth token from login endpoint
        const loginResponse = await loginService({ email, password, mfa_token });
        const authToken = loginResponse.data.data.auth_token;

        // Step 2: Set auth token immediately so it's available for subsequent requests
        dispatch({
          type: SET_AUTH_TOKEN,
          payload: {
            authToken: authToken,
            isKeycloakAuth: false,
          },
        });

        // Step 3: Fetch full user data from /user endpoint (standardized format)
        const userResponse = await validLoginService();
        const userData = userResponse.data.data;

        // Step 4: Dispatch LOGIN with standardized user data
        const payload = {
          auth_token: authToken,
          user: userData,
          isKeycloakAuth: false,
        };

        dispatch({
          type: LOGIN,
          payload: payload,
        });

        onSuccess && onSuccess(userData);
      } catch (e) {
        console.error('[actLogin] Error:', e);
        onError && onError(e);
      }
    };

  const actVerifyCredentials = async ({ email, password }, onSuccess, onError) => {
    try {
      const response = await loginService({ email, password });
      // Mode 1: Backend returns 200 with credentials_valid: true/false
      if (response.data.success && response.data.credentials_valid) {
        onSuccess && onSuccess(response.data);
      } else {
        onError && onError(response.data);
      }
    } catch (e) {
      // 403 or other error response
      onError && onError(e);
    }
  };

  const actSendMfaToken = async ({ email }, onSuccess, onError) => {
    try {
      const response = await sendMfaTokenService({ email });
      onSuccess && onSuccess(response);
    } catch (e) {
      onError && onError(e);
    }
  };

  const actSetMfaPending = ({ email, password }) => (dispatch) => {
    dispatch({
      type: SET_MFA_PENDING,
      payload: {
        email,
        password,
        timestamp: Date.now(),
      },
    });
  };

  const actClearMfaPending = () => (dispatch) => {
    dispatch({
      type: CLEAR_MFA_PENDING,
    });
  };

  const actForgotPassword = async ({ email }, onSuccess, onError) => {
    try {
      const response = await forgotPasswordService({ email });
      onSuccess && onSuccess(response);
    } catch (e) {
      onError && onError(e);
    }
  };

  const actRecoveryPassword = async ({ email, password, token }, onSuccess, onError) => {
    try {
      const response = await recoveryPasswordService({ email, password, token });
      onSuccess && onSuccess(response);
    } catch (e) {
      onError && onError(e);
    }
  };

  const actRecoveryPasswordApp = async ({ email, password, tokenApp }, onSuccess, onError) => {
    try {
      const response = await recoveryPassworAppdService({ email, password, tokenApp });
      onSuccess && onSuccess(response);
    } catch (e) {
      onError && onError(e);
    }
  };

  const actValidLoginPassword = () => async (dispatch) => {
    try {
      const response = await validLoginService();
      const userData = response.data.data;

      // Update session with fresh user data, especially country_id
      dispatch({
        type: UPDATE_SESSION,
        payload: {
          country_id: userData.country_id || [],
          name: userData.name,
          lastname: userData.last_name,
          email: userData.email,
          rol: userData.rol?.[0]?.name || '',
        },
      });

      return true; // Validation succeeded
    } catch (e) {
      return false;
    }
  };

  const actValidKeycloakSession = () => {
    try {
      const { isKeycloakAuthenticated } = useKeycloak();
      return isKeycloakAuthenticated();
    } catch (e) {
      return false;
    }
  };

  const actLogout = (token, onSuccess, onError) => async (dispatch) => {
    try {
      const response = await logOutService({ token });
      dispatch({
        type: LOGOUT,
      });
      // Also logout from Keycloak if authenticated
      keycloakLogout();
      onSuccess && onSuccess(response.data);
    } catch (e) {
      onError && onError(e);
    }
  };

  const actKeycloakLogin = (onSuccess, onError) => async (dispatch) => {
    try {
      keycloakLogin(
        async (keycloak) => {
          const token = getKeycloakToken();

          try {
            dispatch({
              type: SET_AUTH_TOKEN,
              payload: {
                authToken: token,
                isKeycloakAuth: true,
              },
            });

            // Fetch user data from /user endpoint (standardized format)
            const profileResponse = await validLoginService();
            const userData = profileResponse.data.data;

            // Use standardized data from /user endpoint directly
            const payload = {
              auth_token: token,
              user: userData,
              isKeycloakAuth: true,
            };

            dispatch({
              type: LOGIN,
              payload: payload,
            });

            onSuccess && onSuccess(userData);
          } catch (profileError) {
            onError && onError(profileError);
          }
        },
        (error) => {
          onError && onError(error);
        }
      );
    } catch (e) {
      onError && onError(e);
    }
  };

  /**
   * Refresh user data from server and update Redux state
   * Called by NavBar on every page load to sync latest user changes
   * This ensures role/permission/country changes take effect immediately without re-login
   */
  const actRefreshPermissions = () => async (dispatch) => {
    try {
      const response = await validLoginService();
      const userData = response.data.data;

      // Extract permissions from first role and filter only ALLOW type
      // Note: null type is treated as ALLOW (inherited from role)
      const allPermissions = userData.rol?.[0]?.permissions || [];
      const permissions = allPermissions.filter(p => p.type === 'ALLOW' || p.type === null);

      // Update full session data from /user endpoint
      // This ensures user changes (name, email, rol, permissions, countries)
      // take effect immediately without re-login
      dispatch({
        type: UPDATE_SESSION,
        payload: {
          name: userData.name,
          lastname: userData.last_name,
          email: userData.email,
          rol: userData.rol?.[0]?.name || '',
          country_id: userData.country_id || [],
          permissions: permissions,
          permissionsLoaded: true,
        },
      });

      return permissions;
    } catch (e) {
      console.error('[Auth Actions] Failed to refresh user data:', e);
      return [];
    }
  };

  return {
    actLogin,
    actVerifyCredentials,
    actSendMfaToken,
    actSetMfaPending,
    actClearMfaPending,
    actForgotPassword,
    actRecoveryPassword,
    actValidLoginPassword,
    actValidKeycloakSession,
    actLogout,
    actRecoveryPasswordApp,
    actKeycloakLogin,
    actRefreshPermissions,
  };
};

export default useAuthActions;
